import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, moneyCompact, num, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { OutcomeDialog } from "@/components/OutcomeDialog";
import { ContactDialog } from "@/components/ContactDialog";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkBar, BulkBtn, RowCheck } from "@/components/BulkBar";
import { BulkActionDialog } from "@/components/BulkActionDialog";
import { ReassignDialog } from "@/components/ReassignDialog";
import { IntentChip } from "@/pages/Inbox";
import { NextAction } from "@/components/PhaseChip";
import { EngagementChip, EngagementStateChip, AssigneeAvatar } from "@/components/EngagementChip";
import { RoundChip, RocketFuelLink, DueDates } from "@/components/BidOpsChips";
import { toast } from "sonner";
import {
  Hourglass, Clock, ArrowRight, BellRing, MessageSquare, Reply, Send,
  Snowflake, AlarmClockOff, RotateCcw, Trophy, XCircle, Download, UserPlus, Trash2,
} from "lucide-react";

const agingChip = (d) => {
  if (d === null || d === undefined) return { cls: "bg-slate-100 text-slate-500", label: "no date" };
  if (d > 90) return { cls: "bg-rose-100 text-rose-700", label: `${d}d waiting` };
  if (d > 30) return { cls: "bg-amber-100 text-amber-700", label: `${d}d waiting` };
  return { cls: "bg-emerald-50 text-emerald-700", label: `${d}d waiting` };
};

const ACTIONS = {
  reply: { action: "contact_in", title: "Log client reply", needsNote: true, noteLabel: "What did they say?", notePlaceholder: "Optional — e.g. 'Asked for revised schedule'", confirmLabel: "Log reply" },
  followup: { action: "contact_out", title: "Log follow-up sent", needsNote: true, noteLabel: "Note", notePlaceholder: "Optional — e.g. 'Emailed to check status'", confirmLabel: "Log follow-up" },
  snooze: { action: "snooze", title: "Snooze follow-up", needsDays: true, description: "Stops the overdue nudge for the chosen number of days. Bids stay on the waiting list.", confirmLabel: "Snooze" },
  cold: { action: "cold", title: "Mark gone cold", needsNote: true, noteRequired: false, noteLabel: "Reason (optional)", description: "Drops these off the waiting list. Not counted as a loss — revive anytime.", tone: "danger", confirmLabel: "Mark cold" },
  won: { action: "outcome", outcome: "won", title: "Record Won", needsNote: true, noteRequired: false, noteLabel: "Note (optional)", confirmLabel: "Mark Won" },
  lost: { action: "outcome", outcome: "lost", title: "Record Lost", needsNote: true, noteRequired: true, noteLabel: "Loss reason", presets: true, tone: "danger", confirmLabel: "Mark Lost" },
  revive: { action: "revive", title: "Revive to waiting", description: "Puts these back on the active waiting list and clears any snooze.", confirmLabel: "Revive" },
};

export default function Awaiting() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("waiting"); // waiting | cold
  const [outcomeBid, setOutcomeBid] = useState(null);
  const [contactBid, setContactBid] = useState(null);
  const [dlg, setDlg] = useState(null); // active bulk action config
  const [reassign, setReassign] = useState(false);
  const sel = useRowSelection();

  const exportSel = async () => {
    try {
      const res = await api.post("/bids/export", { bid_ids: sel.ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "estimate-waiting.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const doReassign = async (uid) => {
    try {
      const res = await api.post("/bids/bulk", { action: "reassign", bid_ids: sel.ids, assignee_id: uid });
      toast.success(`Reassigned ${res.data.updated} bid${res.data.updated === 1 ? "" : "s"}`);
      setReassign(false); sel.clear(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reassign failed"); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.count} bid${sel.count === 1 ? "" : "s"} permanently? This removes the estimate and all its data.`)) return;
    try {
      const res = await api.post("/bids/bulk", { action: "delete", bid_ids: sel.ids });
      toast.success(`Deleted ${res.data.deleted} bid${res.data.deleted === 1 ? "" : "s"}`);
      sel.clear(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/pipeline/waiting", { params: { cold: view === "cold" } });
      setItems(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { sel.clear(); load(); /* eslint-disable-next-line */ }, [view]);

  const orderedIds = items.map((b) => b.bid_id);
  const totalValue = items.reduce((a, b) => a + (b.stored_total || 0), 0);
  const oldest = items[0]?.days_waiting;
  const allSelected = items.length > 0 && sel.count === items.length;

  const runBulk = async ({ note, days }) => {
    const cfg = dlg;
    try {
      const res = await api.post("/bids/bulk", {
        action: cfg.action, bid_ids: sel.ids, note, days, outcome: cfg.outcome,
      });
      const n = res.data.updated ?? res.data.deleted ?? 0;
      toast.success(`${cfg.confirmLabel} — ${n} bid${n === 1 ? "" : "s"}`);
      setDlg(null); sel.clear(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
  };

  return (
    <div className="px-6 sm:px-8 py-8 pb-24">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
            <Hourglass className="w-5 h-5 text-[#003E87]" /> Bids waiting
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Every submitted bid still awaiting a decision, longest wait first. Select any number to act in bulk.</p>
        </div>
        <div className="flex gap-6 text-right transition-opacity" style={{ opacity: items.length === 0 ? 0.5 : 1 }}>
          <div><div className="font-display text-xl font-bold text-black tabular-nums">{items.length}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">{view === "cold" ? "Cold" : "Out"}</div></div>
          <div><div className="font-display text-xl font-bold text-black tabular-nums">{moneyCompact(totalValue)}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">In play</div></div>
          <div><div className="font-display text-xl font-bold text-black tabular-nums">{oldest != null ? `${oldest}d` : "—"}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">Oldest</div></div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {[["waiting", "Waiting"], ["cold", "Gone cold"]].map(([k, l]) => (
            <button key={k} data-testid={`waiting-view-${k}`} onClick={() => setView(k)}
              className={`text-sm px-3 py-1.5 rounded-md transition ${view === k ? "bg-white shadow-sm text-black font-medium" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
        {items.length > 0 && (
          <div data-testid="waiting-select-all" role="button" tabIndex={0} onClick={() => sel.selectAll(orderedIds)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 cursor-pointer">
            <RowCheck checked={allSelected} onClick={() => {}} testid="waiting-select-all-check" />
            {allSelected ? "Clear all" : "Select all"}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <Clock className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-600">{view === "cold" ? "No bids parked as cold." : "No bids are waiting on a decision."}</div>
          <div className="text-xs text-slate-400 mt-1">{view === "cold" ? "Bids you mark gone cold land here — revive them anytime." : "Submitted bids land here until you record the outcome."}</div>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((b, i) => {
            const chip = agingChip(b.days_waiting);
            const checked = sel.isSelected(b.bid_id);
            return (
              <Card key={b.bid_id} data-testid={`waiting-row-${b.bid_id}`}
                className={`flex items-center gap-4 p-4 transition ${checked ? "border-[#003E87] bg-[#003E87]/[0.03]" : "hover:border-[#003E87]/40 hover:shadow-sm"}`}>
                <RowCheck checked={checked} testid={`waiting-check-${b.bid_id}`}
                  onClick={(e) => sel.toggle(b.bid_id, i, e.shiftKey, orderedIds)} />
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${chip.cls}`}>{chip.label}</span>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/bids/${b.bid_id}`)}>
                  <div className="text-sm font-medium text-black truncate flex items-center gap-1.5 flex-wrap">
                    {b.name}
                    <RoundChip revision={b.revision} sentAt={b.sent_at} testid={`waiting-round-${b.bid_id}`} />
                    <RocketFuelLink url={b.rocketfuel_url} testid={`waiting-rocketfuel-${b.bid_id}`} />
                    {b.cold && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 rounded px-1.5 py-0.5"><Snowflake className="w-2.5 h-2.5" /> Cold</span>}
                    {b.snoozed_until && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5"><AlarmClockOff className="w-2.5 h-2.5" /> snoozed to {fmtDate(b.snoozed_until)}</span>}
                    {b.owed && <span data-testid={`owed-${b.bid_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#8a6d00] bg-[#F4C400]/15 rounded px-1.5 py-0.5"><BellRing className="w-2.5 h-2.5" /> {b.owed_reason}</span>}
                    {b.latest_intent && <span data-testid={`waiting-intent-${b.bid_id}`}><IntentChip intent={b.latest_intent} /></span>}
                    <EngagementChip label={b.engagement} replyRate={b.reply_rate_pct} testid={`waiting-engagement-${b.bid_id}`} />
                    {["quiet", "chasing"].includes(b.engagement_state) && (
                      <EngagementStateChip state={b.engagement_state} silenceDays={b.silence_days} followups={b.unanswered_followups} testid={`waiting-state-${b.bid_id}`} />
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {b.client_name || "No client"} · {num(b.capacity_mw)} MW{b.state ? ` · ${b.state}` : ""}
                    {b.sent_at ? ` · sent ${fmtDate(b.sent_at)}` : ""}
                    {b.award_expected_date ? ` · award expected ${fmtDate(b.award_expected_date)}` : ""}
                  </div>
                  <DueDates due={b.due_date} internal={b.internal_due_date} testid={`waiting-duedates-${b.bid_id}`} />
                  <NextAction signal={b.signal} testid={`waiting-action-${b.bid_id}`} />
                </div>
                <div className="text-sm font-mono font-medium text-slate-700 shrink-0">{b.stored_total ? money(b.stored_total) : "—"}</div>
                <AssigneeAvatar assignee={b.assignee} size="w-7 h-7" testid={`waiting-assignee-${b.bid_id}`} />
                {view === "waiting" && (
                  <Button size="sm" variant="outline" data-testid={`waiting-contact-${b.bid_id}`} onClick={() => setContactBid(b)} className="shrink-0">
                    <MessageSquare className="w-3.5 h-3.5 mr-1" /> Log contact
                  </Button>
                )}
                <Button size="sm" variant="outline" data-testid={`waiting-outcome-${b.bid_id}`} onClick={() => setOutcomeBid(b)} className="shrink-0">
                  Record outcome
                </Button>
                <ArrowRight className="w-4 h-4 text-slate-300 shrink-0 cursor-pointer" onClick={() => navigate(`/bids/${b.bid_id}`)} />
              </Card>
            );
          })}
        </div>
      )}

      <BulkBar count={sel.count} onClear={sel.clear}>
        {view === "waiting" ? (
          <>
            <BulkBtn testid="bulk-reply" icon={Reply} onClick={() => setDlg(ACTIONS.reply)}>Log reply</BulkBtn>
            <BulkBtn testid="bulk-followup" icon={Send} onClick={() => setDlg(ACTIONS.followup)}>Follow-up</BulkBtn>
            <BulkBtn testid="bulk-snooze" icon={AlarmClockOff} onClick={() => setDlg(ACTIONS.snooze)}>Push a week</BulkBtn>
            <BulkBtn testid="bulk-cold" icon={Snowflake} onClick={() => setDlg(ACTIONS.cold)}>Gone cold</BulkBtn>
            <BulkBtn testid="bulk-won" icon={Trophy} tone="green" onClick={() => setDlg(ACTIONS.won)}>Won</BulkBtn>
            <BulkBtn testid="bulk-lost" icon={XCircle} tone="danger" onClick={() => setDlg(ACTIONS.lost)}>Lost</BulkBtn>
            <BulkBtn testid="bulk-reassign" icon={UserPlus} onClick={() => setReassign(true)}>Reassign</BulkBtn>
            <BulkBtn testid="bulk-export" icon={Download} onClick={exportSel}>Export</BulkBtn>
            <BulkBtn testid="bulk-delete" icon={Trash2} tone="danger" onClick={bulkDelete}>Delete</BulkBtn>
          </>
        ) : (
          <BulkBtn testid="bulk-revive" icon={RotateCcw} tone="gold" onClick={() => setDlg(ACTIONS.revive)}>Revive to waiting</BulkBtn>
        )}
      </BulkBar>

      <ReassignDialog open={reassign} onOpenChange={setReassign} count={sel.count} onConfirm={doReassign} />

      <BulkActionDialog open={!!dlg} onOpenChange={(o) => { if (!o) setDlg(null); }} config={dlg} count={sel.count} onConfirm={runBulk} />

      <OutcomeDialog bidId={outcomeBid?.bid_id} bidName={outcomeBid?.name}
        open={!!outcomeBid} onOpenChange={(o) => { if (!o) setOutcomeBid(null); }}
        onDone={() => { setOutcomeBid(null); load(); }} />
      <ContactDialog bidId={contactBid?.bid_id} bidName={contactBid?.name}
        open={!!contactBid} onOpenChange={(o) => { if (!o) setContactBid(null); }}
        onDone={() => { setContactBid(null); load(); }} />
    </div>
  );
}
